import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { CustomerApi, MainApi } from '_api';
import { MODAL_KEYS } from '_constants/modals';
import { handleError, handleResponse } from '_helpers/api';
import { hideModal } from '_store/modals/slice';

export const getPaymentMethods = createAsyncThunk(
  'billing/getPaymentMethods',
  async function ({ customerId }, { rejectWithValue }) {
    try {
      const response = await CustomerApi.getPaymentMethods({ customerId });
      return handleResponse(response);
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const createPaymentMethod = createAsyncThunk(
  'billing/createPaymentMethod',
  async function ({ customerId, body }, { dispatch, rejectWithValue }) {
    try {
      await CustomerApi.createPaymentMethod({ customerId, body });
      toast.success('Payment method created successfully!');
      dispatch(getPaymentMethods({ customerId }));
      dispatch(hideModal({ key: MODAL_KEYS.paymentMethodAdd }));
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const getSubscriptions = createAsyncThunk(
  'billing/getSubscriptions',
  async function ({ customerId }, { rejectWithValue }) {
    try {
      const response = await CustomerApi.getSubscriptions({ customerId });
      return handleResponse(response);
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'billing/cancelSubscription',
  async function ({ customerId, orderId, productId, productName }, { dispatch, rejectWithValue }) {
    try {
      await MainApi.cancelSubscription({ orderId, productId });
      toast.success(
        `Your ${productName} subscription has been successfully cancelled, you will not receive any further charges for it.`
      );
      dispatch(getSubscriptions({ customerId }));
      dispatch(hideModal({ key: MODAL_KEYS.cancelSubscriptionConfirm }));
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const getSubscriptionsDetails = createAsyncThunk(
  'billing/getSubscriptionsDetails',
  async function ({ orders = [] }, { fulfillWithValue, rejectWithValue }) {
    try {
      const subscriptionProducts = [];

      orders.forEach((order) => {
        order?.products?.forEach((product) => {
          if (product?.code?.subscription) {
            subscriptionProducts.push({ ...product, crmOrderId: order?.crmOrderId });
          }
        });
      });

      const requests = subscriptionProducts.map((product) =>
        MainApi.getSubscriptionDetails({ orderId: product?.orderId, productId: product?.id })
      );

      const result = await Promise.all(requests)
        .then((responses) => {
          const payload = responses.map(({ data }, idx) => ({ ...data, product: subscriptionProducts[idx] }));

          return fulfillWithValue(payload);
        })
        .catch((error) => {
          const processedError = handleError(error);
          return rejectWithValue(processedError);
        });

      return result;
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

const billingSlice = createSlice({
  name: 'billing',
  initialState: {
    paymentMethods: {
      data: [],
      error: null,
      loading: false,
      creating: false,
    },
    subscriptions: {
      data: [],
      error: null,
      loading: false,
      canceling: false,
    },
    subscriptionsDetails: {
      data: [],
      error: null,
      loading: false,
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentMethods.pending, (state) => {
        state.paymentMethods.data = [];
        state.paymentMethods.error = null;
        state.paymentMethods.loading = true;
      })
      .addCase(getPaymentMethods.fulfilled, (state, action) => {
        state.paymentMethods.data = action.payload;
        state.paymentMethods.loading = false;
      })
      .addCase(getPaymentMethods.rejected, (state, action) => {
        state.paymentMethods.error = action.payload;
        state.paymentMethods.loading = false;
      });

    builder
      .addCase(createPaymentMethod.pending, (state) => {
        state.paymentMethods.creating = true;
      })
      .addCase(createPaymentMethod.fulfilled, (state) => {
        state.paymentMethods.creating = false;
      })
      .addCase(createPaymentMethod.rejected, (state) => {
        state.paymentMethods.creating = false;
      });

    builder
      .addCase(getSubscriptions.pending, (state) => {
        state.subscriptions.data = [];
        state.subscriptions.error = null;
        state.subscriptions.loading = true;
      })
      .addCase(getSubscriptions.fulfilled, (state, action) => {
        state.subscriptions.data = action.payload;
        state.subscriptions.loading = false;
      })
      .addCase(getSubscriptions.rejected, (state, action) => {
        state.subscriptions.error = action.payload;
        state.subscriptions.loading = false;
      });

    builder
      .addCase(getSubscriptionsDetails.pending, (state) => {
        state.subscriptionsDetails.data = [];
        state.subscriptionsDetails.error = null;
        state.subscriptionsDetails.loading = true;
      })
      .addCase(getSubscriptionsDetails.fulfilled, (state, action) => {
        state.subscriptionsDetails.data = action.payload;
        state.subscriptionsDetails.loading = false;
      })
      .addCase(getSubscriptionsDetails.rejected, (state, action) => {
        state.subscriptionsDetails.error = action.payload;
        state.subscriptionsDetails.loading = false;
      });

    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.subscriptions.canceling = true;
      })
      .addCase(cancelSubscription.fulfilled, (state) => {
        state.subscriptions.canceling = false;
      })
      .addCase(cancelSubscription.rejected, (state) => {
        state.subscriptions.canceling = false;
      });
  },
});

export default billingSlice.reducer;
